import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Categorical series colors in fixed order (validated reference palette). Never cycle. */
export const SERIES = ["var(--series-1)", "var(--series-2)", "var(--series-3)", "var(--series-4)", "var(--series-5)", "var(--series-6)", "var(--series-7)", "var(--series-8)"] as const;

/** Tiny inline trend line. Decorative-plus: always paired with the number it summarizes. */
export function Sparkline({ data, width = 88, height = 24, color = "var(--series-1)", className, label }: { data: number[]; width?: number; height?: number; color?: string; className?: string; label?: string }) {
  if (data.length < 2) return <span className="text-xs text-fg-3">—</span>;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = max - min || 1;
  const step = width / (data.length - 1);
  const pts = data.map((v, i) => [i * step, height - 3 - ((v - min) / span) * (height - 6)] as const);
  const d = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const last = pts[pts.length - 1]!;
  return (
    <svg width={width} height={height} className={cn("overflow-visible", className)} role="img" aria-label={label ?? `Trend from ${data[0]} to ${data[data.length - 1]}`}>
      <path d={d} fill="none" stroke={color} strokeWidth={1.75} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r={2.5} fill={color} />
    </svg>
  );
}

/** Shared Recharts tooltip body — text in ink tokens, a colored key carries identity. */
export function ChartTooltip({ active, payload, label, formatLabel, formatValue }: { active?: boolean; payload?: Array<{ name?: string; value?: number | string; color?: string; dataKey?: string | number }>; label?: string | number; formatLabel?: (l: string | number) => ReactNode; formatValue?: (v: number, name: string) => ReactNode }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border bg-surface px-2.5 py-2 text-xs shadow-xl">
      {label !== undefined ? <div className="mb-1 font-medium text-fg">{formatLabel ? formatLabel(label) : label}</div> : null}
      <div className="flex flex-col gap-0.5">
        {payload.map((p) => (
          <div key={String(p.dataKey ?? p.name)} className="flex items-center gap-2">
            <span className="size-2 rounded-sm" style={{ background: p.color }} />
            <span className="text-fg-2">{p.name}</span>
            <span className="ml-auto pl-3 font-medium tabular text-fg">{formatValue ? formatValue(Number(p.value), String(p.name)) : p.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Legend({ items }: { items: Array<{ label: string; color: string }> }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-2">
      {items.map((i) => (
        <span key={i.label} className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-sm" style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}
